<template>
    <UseFiltersTemplate>
        <div class="grid items-start gap-6">
            <div class="grid gap-2">
                <label class="text-sm font-semibold text-gray-800 dark:text-white">Category</label>
                <div class="flex flex-wrap gap-2">
                    <button v-for="(category, index) in categories" :key="index" :class="cn('dark:text-gray-400 bg-[#f5f5f5] dark:bg-[#2c2e32] hover:bg-neutral-900 dark:hover:bg-[#3b3d41] hover:text-white transition transition-colors duration-300 py-2 px-4 text-sm font-medium rounded-md', {'bg-neutral-900 dark:bg-[#3b3d41] text-white dark:text-white': tempFilters.category.includes(category.name) })" @click="toggleTempCategoryFilter(category.name)">
                        {{ category.name }}
                    </button>
                </div>
            </div>
            <div class="grid gap-2">
                <label class="text-sm font-semibold text-gray-800 dark:text-white">Rarity</label>
                <div class="flex flex-wrap gap-2">
                    <button v-for="(rarity, index) in rarities" :key="index" :class="cn('dark:text-gray-400 bg-[#f5f5f5] dark:bg-[#2c2e32] hover:bg-neutral-900 dark:hover:bg-[#3b3d41] hover:text-white transition transition-colors duration-300 py-2 px-4 text-sm font-medium rounded-md', {'bg-neutral-900 dark:bg-[#3b3d41] text-white dark:text-white': tempFilters.rarity.includes(rarity.name) })" @click="toggleTempRarityFilter(rarity.name)">
                        {{ rarity.name }}
                    </button>
                </div>
            </div>
            <div class="grid gap-2">
                <label class="text-sm font-semibold text-gray-800 dark:text-white">Demand</label>
                <div class="flex flex-wrap gap-2"> 
                    <button v-for="(demand, index) in demands" :key="index" :class="cn('dark:text-gray-400 bg-[#f5f5f5] dark:bg-[#2c2e32] hover:bg-neutral-900 dark:hover:bg-[#3b3d41] hover:text-white transition transition-colors duration-300 py-2 px-4 text-sm font-medium rounded-md', {'bg-neutral-900 dark:bg-[#3b3d41] text-white dark:text-white': tempFilters.demand.includes(demand.name) })" @click="toggleTempDemandFilter(demand.name)">
                        {{ demand.name }}
                    </button>
                </div>
            </div>
            <div class="grid gap-2">
                <label class="text-sm font-semibold text-gray-800 dark:text-white">Value</label>
                <div class="flex gap-x-4">
                    <div class="grid gap-2 flex-1">
                        <label class="text-sm font-semibold text-gray-800 dark:text-white" for="minValue">Min</label>
                        <InputNumber id="minValue" name="min" v-model="tempFilters.minValue" />
                    </div>
                    <div class="grid gap-2 flex-1">
                        <label class="text-sm font-semibold text-gray-800 dark:text-white" for="maxValue">Max</label>
                        <InputNumber id="maxValue" name="max" v-model="tempFilters.maxValue"  />
                    </div>
                </div>
            </div>
            <div class="grid gap-2">
                <label class="text-sm font-semibold text-gray-800 dark:text-white">Addons</label>
                <div class="flex flex-wrap gap-2">
                    <button v-for="(addon, index) in addons" :key="index" :class="cn('dark:text-gray-400 bg-[#f5f5f5] dark:bg-[#2c2e32] hover:bg-neutral-900 dark:hover:bg-[#3b3d41] hover:text-white transition transition-colors duration-300 py-2 px-4 text-sm font-medium rounded-md', {'bg-neutral-900 dark:bg-[#3b3d41] text-white dark:text-white': tempFilters.addon.includes(addon.name) })" @click="toggleTempAddonFilter(addon.name)">
                        <NuxtImg class="h-14 mx-auto object-contain" :src="addon.icon" />
                    </button>
                </div>
            </div>
        </div>
    </UseFiltersTemplate>
    <UseFilterTriggerTemplate>
        <div class="relative">
            <ShadButton variant="outline" :class="{ 'border-0 ring-2 ring-inset ring-black dark:ring-white': appliedFilterCount > 0 }">
                <LucideSlidersHorizontal :size="14" />
                <span class="ml-2">Filters</span>
            </ShadButton>
            <span v-if="appliedFilterCount > 0" class="absolute top-[-8px] right-[-8px] bg-black dark:bg-white dark:text-black text-white text-[10px] font-semibold rounded-full w-5 h-5 flex items-center justify-center">
                {{ appliedFilterCount }}
            </span>
        </div>
    </UseFilterTriggerTemplate>
    <div>
        <div class="flex gap-x-2.5">
            <ShadInput class="flex-1" placeholder="Search by name..." @input="emit('update:name', $event.target.value)" />
            <ShadDialog v-if="viewport.isGreaterThan('sm')" v-model:open="isOpen">
                <ShadDialogTrigger as-child>
                    <FilterTrigger />
                </ShadDialogTrigger>
                <ShadDialogContent class="block sm:max-w-[600px] p-0">
                    <ShadDialogHeader class="py-6 px-5 border-b border-neutral-200 dark:border-white/5">
                        <ShadDialogTitle>Filters</ShadDialogTitle>
                    </ShadDialogHeader>
                    <div class="py-6 px-6 overflow-y-auto max-h-[250px] sm:max-h-[550px]">
                        <Filters />
                    </div>
                    <ShadDialogFooter class="py-4 px-5 sm:justify-between border-t border-neutral-200 dark:border-white/5">
                        <ShadButton variant="outline" @click="resetFilters">
                            Reset
                        </ShadButton>
                        <ShadButton @click="applyFilters">
                            Apply
                        </ShadButton>
                    </ShadDialogFooter>
                </ShadDialogContent>
            </ShadDialog>
            <ShadDrawer v-else v-model:open="isOpen">
                <ShadDrawerTrigger as-child>
                    <FilterTrigger />
                </ShadDrawerTrigger>
                <ShadDrawerContent>
                    <ShadDrawerHeader class="text-left px-6">
                        <ShadDrawerTitle>Filters</ShadDrawerTitle>
                    </ShadDrawerHeader>
                    <div class="pt-2 pb-6 px-6 overflow-y-auto max-h-[400px] sm:max-h-[550px]">
                        <Filters />
                    </div>
                    <ShadDrawerFooter class="py-6 px-6 border-t border-neutral-200 dark:border-white/5 flex-row justify-between">
                        <ShadButton variant="outline" @click="resetFilters">
                            Reset
                        </ShadButton>
                        <ShadButton @click="applyFilters">
                            Apply
                        </ShadButton>
                    </ShadDrawerFooter>
                </ShadDrawerContent>
            </ShadDrawer>
        </div>
    </div>
</template>

<script setup lang="ts">
import { cloneDeep } from 'lodash-es'
import { cn } from '~/lib/utils'

const props = defineProps({
    name: {
        type: String,
        required: false
    },
    category: {
        type: Array,
        required: true
    },
    rarity: {
        type: Array,
        required: true
    },
    demand: {
        type: Array,
        required: true
    },
    addon: {
        type: Array,
        required: true
    },
    minValue: {
        type: [Number, String],
        required: false
    },
    maxValue: {
        type: [Number, String],
        required: false
    }
})

const [UseFiltersTemplate, Filters] = createReusableTemplate()
const [UseFilterTriggerTemplate, FilterTrigger] = createReusableTemplate()

const viewport = useViewport()
const isOpen = ref(false)

const defaultFilters = {
    name: '',
    category: [] as string[],
    rarity: [] as string[],
    demand: [] as string[],
    minValue: undefined,
    maxValue: undefined,
    addon: [] as string[]
}

const tempFilters = ref(cloneDeep(defaultFilters))

const { data: categories } = await useFetch<{ name: string }[]>('/api/ps-99/categories')
const { data: rarities } = await useFetch<{ name: string }[]>('/api/ps-99/rarities')
const { data: demands } = await useFetch<{ name: string }[]>('/api/demands')
const { data: addons } = await useFetch<{ name: string, icon: string }[]>('/api/ps-99/addons')

const applyFilters = () => {
    emit('update:category', [...tempFilters.value.category])
    emit('update:rarity', [...tempFilters.value.rarity])
    emit('update:demand', [...tempFilters.value.demand])
    emit('update:addon', [...tempFilters.value.addon])
    emit('update:minValue', tempFilters.value.minValue)
    emit('update:maxValue', tempFilters.value.maxValue)
    isOpen.value = false
}

// Compute the number of applied filters
const appliedFilterCount = computed(() => {
    let count = 0;
    if (props.category.length > 0) count += props.category.length; // Non-empty category array
    if (props.rarity.length > 0) count += props.rarity.length; // Non-empty rarity array
    if (props.demand.length > 0) count += props.demand.length; // Non-empty demand array
    if (props.minValue) count++; // minValue is set
    if (props.maxValue) count++; // maxValue is set
    if (props.addon.length > 0) count += props.addon.length; // Non-empty demand array
    return count;
});

const resetFilters = () => {
    tempFilters.value = cloneDeep(defaultFilters)
}

const toggleTempCategoryFilter = (category: string) => {
    if (tempFilters.value.category.includes(category)) {
        tempFilters.value.category = tempFilters.value.category.filter((c: string) => c !== category)
    } else {
        tempFilters.value.category.push(category)
    }
}

const toggleTempRarityFilter = (rarity: string) => {
    if (tempFilters.value.rarity.includes(rarity)) {
        tempFilters.value.rarity = tempFilters.value.rarity.filter((c: string) => c !== rarity)
    } else {
        tempFilters.value.rarity.push(rarity)
    }
}

const toggleTempDemandFilter = (demand: string) => {
    if (tempFilters.value.demand.includes(demand)) {
        tempFilters.value.demand = tempFilters.value.demand.filter((d: string) => d !== demand)
    } else {
        tempFilters.value.demand.push(demand)
    }
}

const toggleTempAddonFilter = (addon: string) => {
    if (tempFilters.value.addon.includes(addon)) {
        tempFilters.value.addon = tempFilters.value.addon.filter((d: string) => d !== addon)
    } else {
        tempFilters.value.addon.push(addon)
    }
}

const emit = defineEmits(['update:name', 'update:category', 'update:rarity', 'update:demand', 'update:addon', 'update:minValue', 'update:maxValue'])
</script>